import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetRecordableOperationsResponse } from '@/api/handlers/resources/operations/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { OperationsQueryKeys } from './operationsQueryKeys';

const STALE_TIME_MS = 60_000;

/**
 * The operations this session may record on a date.
 *
 * **Keyed on the date, and refetched when it moves**, because the answer is a
 * function of it: which version is in force is resolved from the entry's own
 * date, so a form built for last month belongs to last month's recipe. A screen
 * that kept the list while the user changed the date would be offering a form it
 * is not about to submit.
 *
 * Shorter `staleTime` than the catalog's five minutes. This list is filtered by
 * the caller's role and by what is published *today*, and an OrgAdmin who has
 * just published a version or unblocked an operation in the next tab expects the
 * picker to know. A minute is short enough for that and long enough that moving
 * the date back and forth is instant.
 *
 * `enabled` is the caller's, the pattern every accounting query here follows:
 * access is derived from the session before fetching, because a raw 403 tells
 * the user nothing they can act on.
 */
export const useRecordableOperationsQuery = (
    date: string,
    enabled: boolean,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetRecordableOperationsResponse>>({
        queryKey: OperationsQueryKeys.recordable(date),
        queryFn: () => apiClient.operations.getRecordable(date),
        enabled: enabled && date.length > 0,
        staleTime: STALE_TIME_MS,
    });
};
